/* Narwal's source media contract, with explicit lifecycle and motion preferences. */
(() => {
  'use strict';
  if (window.NarwalMotion) { window.NarwalMotion.init(document); return; }

  const controllers = new Map();
  const entrances = new Map();
  const navigations = new Map();
  const manualVideos = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const rootSelector = '[data-ndev-media]';
  const eachRoot = (scope, callback) => {
    if (scope instanceof Element && scope.matches(rootSelector)) callback(scope);
    scope.querySelectorAll(rootSelector).forEach(callback);
  };

  function createMedia(root) {
    if (root.closest('dialog') || controllers.has(root) || (root.__ndevMediaSync && root.hasAttribute('data-ndev-media-init'))) return;
    const configNode = root.querySelector('script[data-ndev-media-settings]');
    const videos = [...root.querySelectorAll('video')];
    if (!configNode || !videos.length) return;
    let config;
    try { config = JSON.parse(configNode.textContent); } catch (_) { return; }

    const events = new AbortController();
    const button = root.closest('.ndev-brand-story-scroll')?.querySelector('[data-nwb-media-toggle]');
    let inView = false;
    let intersecting = false;
    let userPaused = false;
    let userRequestedPlay = false;
    let disposed = false;
    const reset = config.reset_on_resume === true;
    const pause = video => {
      video.pause();
      if (reset && video.currentTime) { try { video.currentTime = 0; } catch (_) {} }
    };
    const visibleVideo = () => videos.find(video => {
      if (video.closest('[hidden], [inert]')) return false;
      const panel = video.closest('.ndev-cpi-tabs__panel, .ndev-x30-sweep-tabs__image-panel');
      if (panel && !panel.classList.contains('active')) return false;
      return video.getClientRects().length > 0 && getComputedStyle(video).visibility !== 'hidden';
    });
    const updateButton = () => {
      if (!button) return;
      const paused = userPaused || (reducedMotion.matches && !userRequestedPlay);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? 'Воспроизвести фоновое видео' : 'Приостановить фоновое видео');
      button.querySelector('[data-nwb-media-toggle-label]').textContent = paused ? 'Воспроизвести видео' : 'Приостановить видео';
    };
    function sync() {
      if (disposed) return;
      const active = visibleVideo();
      const eligible = (inView || (intersecting && userRequestedPlay)) && !document.hidden && !userPaused && (!reducedMotion.matches || userRequestedPlay || manualVideos.has(active));
      videos.forEach(video => {
        if (!eligible || video !== active) { pause(video); return; }
        if (config.autoplay === true || userRequestedPlay) {
          video.muted = true;
          const promise = video.play();
          promise?.catch(() => { /* Keep the approved poster if autoplay is declined. */ });
        }
      });
      updateButton();
    }
    videos.forEach(video => {
      video.playsInline = true;
      video.loop = config.loop === true;
      video.removeAttribute('autoplay');
    });

    const configuredThreshold = Number(config.threshold);
    const threshold = Number.isFinite(configuredThreshold) ? Math.max(0, Math.min(1, configuredThreshold)) : 0.35;
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        intersecting = entries[0].isIntersecting;
        inView = intersecting && entries[0].intersectionRatio >= threshold;
        sync();
      }, { threshold: [0, threshold] });
      observer.observe(root);
    } else { inView = intersecting = true; }

    // Tabs retain the source hidden/active semantics. Observe only this root's
    // ancestors, instead of reinitializing the whole document on every change.
    const visibilityObserver = new MutationObserver(sync);
    for (let node = root; node && node !== document.body; node = node.parentElement) {
      visibilityObserver.observe(node, { attributes: true, attributeFilter: ['hidden', 'inert', 'class'] });
      if (node.classList.contains('shopify-section')) break;
    }
    if (config.toggle_event) {
      document.addEventListener(config.toggle_event, event => {
        const action = event.detail?.action;
        if (action === 'pause') { userPaused = true; }
        else if (action === 'play') { userPaused = false; }
        else if (action === 'toggle') { userPaused = !userPaused; }
        sync();
      }, { signal: events.signal });
    }
    button?.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const shouldPlay = userPaused || (reducedMotion.matches && !userRequestedPlay);
      userPaused = !shouldPlay;
      if (shouldPlay) userRequestedPlay = true;
      if (userRequestedPlay) videos.forEach(video => manualVideos.add(video));
      sync();
    }, { signal: events.signal });

    root.__ndevMediaSync = sync;
    root.setAttribute('data-ndev-media-init', '1');
    root.setAttribute('data-nwb-motion-media', '');
    controllers.set(root, {
      sync,
      destroy() {
        disposed = true;
        observer?.disconnect();
        visibilityObserver.disconnect();
        events.abort();
        videos.forEach(pause);
        if (button) button.hidden = true;
        delete root.__ndevMediaSync;
        root.removeAttribute('data-ndev-media-init');
        root.removeAttribute('data-nwb-motion-media');
        controllers.delete(root);
      }
    });
    sync();
  }

  function createEntrance(root) {
    if (entrances.has(root)) return;
    const previousDuration = root.style.transitionDuration;
    const configuredDuration = Number(root.dataset.aniDuration);
    root.style.transitionDuration = `${Number.isFinite(configuredDuration) && configuredDuration > 0 ? configuredDuration : 1000}ms`;
    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => root.classList.toggle('finish', entry.isIntersecting || reducedMotion.matches));
      }, { threshold: 0 });
      observer.observe(root);
    } else { root.classList.add('finish'); }
    if (reducedMotion.matches) root.classList.add('finish');
    root.setAttribute('data-nwb-motion-entrance', '');
    entrances.set(root, () => {
      observer?.disconnect();
      root.style.transitionDuration = previousDuration;
      root.classList.add('finish');
      root.removeAttribute('data-nwb-motion-entrance');
      entrances.delete(root);
    });
  }
  function eachEntrance(scope, callback) {
    const selector = '[data-ndev-fade-in-out-scroll]';
    if (scope instanceof Element && scope.matches(selector)) callback(scope);
    scope.querySelectorAll(selector).forEach(callback);
  }
  function createNavigation(root) {
    if (navigations.has(root)) return;
    const main = root.closest('#main-content');
    const section = root.closest('.shopify-section');
    const header = document.getElementById('site-header');
    const buttons = [...root.querySelectorAll('[data-position]')];
    if (!main || !section || !buttons.length) return;
    const events = new AbortController();
    const previousTop = section.style.top;
    const originalAttributes = buttons.map(button => ['role', 'tabindex', 'aria-current'].map(name => [name, button.getAttribute(name)]));
    let frame = 0;
    let lockUntil = 0;
    const sections = () => [...main.querySelectorAll(':scope > .shopify-section, :scope > div > .shopify-section')];
    const destination = button => sections()[Number(button.dataset.position) - 1];
    const headerBottom = () => Math.max(0, header?.getBoundingClientRect().bottom || 0);
    const offset = () => headerBottom() + root.offsetHeight + 16;
    function markActive(button) {
      buttons.forEach(item => {
        if (!item.classList.contains('item')) return;
        const active = item === button;
        item.classList.toggle('active', active);
        if (active) item.setAttribute('aria-current', 'location');
        else item.removeAttribute('aria-current');
      });
    }
    function update() {
      frame = 0;
      section.style.top = `${headerBottom()}px`;
      if (performance.now() < lockUntil) return;
      let current;
      buttons.filter(button => button.classList.contains('item')).forEach(button => {
        const target = destination(button);
        if (target && target.getBoundingClientRect().top <= offset() + 24) current = button;
      });
      if (current) markActive(current);
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(update); }
    function activate(button) {
      const target = destination(button);
      if (!target) return;
      lockUntil = performance.now() + 800;
      markActive(button);
      window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + scrollY - offset()), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
    buttons.forEach((button, index) => {
      button.setAttribute('role', 'button');
      button.tabIndex = 0;
      button.addEventListener('click', () => activate(button), { signal: events.signal });
      button.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(button); }
        else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          buttons[(index + (event.key === 'ArrowRight' ? 1 : buttons.length - 1)) % buttons.length].focus();
        }
      }, { signal: events.signal });
    });
    window.addEventListener('scroll', schedule, { passive: true, signal: events.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: events.signal });
    root.setAttribute('data-nwb-navigation-ready', '');
    navigations.set(root, () => {
      events.abort();
      cancelAnimationFrame(frame);
      section.style.top = previousTop;
      buttons.forEach((button, index) => originalAttributes[index].forEach(([name, value]) => {
        if (value === null) button.removeAttribute(name); else button.setAttribute(name, value);
      }));
      root.removeAttribute('data-nwb-navigation-ready');
      navigations.delete(root);
    });
    update();
  }
  function eachNavigation(scope, callback) {
    const selector = '[data-nwb-legacy-navigation]';
    if (scope instanceof Element && scope.matches(selector)) callback(scope);
    scope.querySelectorAll(selector).forEach(callback);
  }
  function init(scope = document) {
    eachRoot(scope, createMedia);
    eachEntrance(scope, createEntrance);
    eachNavigation(scope, createNavigation);
    syncAll();
  }
  function destroy(scope) {
    eachRoot(scope, root => controllers.get(root)?.destroy());
    eachEntrance(scope, root => entrances.get(root)?.());
    eachNavigation(scope, root => navigations.get(root)?.());
  }
  function syncAll() {
    controllers.forEach((controller, root) => {
      if (!root.isConnected) controller.destroy();
      else controller.sync();
    });
    entrances.forEach((teardown, root) => {
      if (!root.isConnected) teardown();
      else if (reducedMotion.matches) root.classList.add('finish');
    });
    if (reducedMotion.matches) {
      document.querySelectorAll('#main-content video').forEach(video => {
        if (!manualVideos.has(video)) video.pause();
      });
    }
  }
  window.NarwalMotion = { init, destroy, sync: syncAll, get mediaCount() { return controllers.size; }, get entranceCount() { return entrances.size; }, get navigationCount() { return navigations.size; } };
  // Existing source controllers retain ownership of their media. This preference
  // guard also catches their automatic play events, without initializing them again.
  document.addEventListener('play', event => {
    const video = event.target;
    if (video instanceof HTMLVideoElement && video.closest('#main-content') && reducedMotion.matches && !manualVideos.has(video)) video.pause();
  }, true);
  function allowNativeVideoControl(event) {
    if (event.isTrusted && event.target instanceof HTMLVideoElement && event.target.controls) manualVideos.add(event.target);
  }
  document.addEventListener('pointerdown', allowNativeVideoControl, true);
  document.addEventListener('keydown', allowNativeVideoControl, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-action-type="popup_video"]');
    if (!button || !event.isTrusted) return;
    const dialog = button.nextElementSibling;
    if (dialog?.tagName === 'DIALOG') dialog.querySelectorAll('video').forEach(video => manualVideos.add(video));
  }, true);
  // Register an explicit, trusted control action before its local play handler.
  // The automatic-play guard remains active for every other video.
  function allowExplicitVideoControl(event) {
    if (!event.isTrusted) return;
    const button = event.target.closest?.('.nwb-s20-video-toggle, .nwb-product-video-toggle, .ndev-instagram-media-card__play, .ndev-home-kol-instagram-card__control--play');
    if (!button) return;
    const root = button.closest('[data-nwb-s20], [data-ndev-instagram-media-card], .ndev-home-kol-instagram-card') || button.parentElement;
    root?.querySelectorAll('video').forEach(video => {
      if (video.getClientRects().length) manualVideos.add(video);
    });
  }
  document.addEventListener('click', allowExplicitVideoControl, true);
  document.addEventListener('visibilitychange', syncAll);
  window.addEventListener('resize', syncAll, { passive: true });
  reducedMotion.addEventListener('change', syncAll);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
